// 部署後「換頁白畫面」自救 (2026-07-24 使用者回報)。
//
// 症狀: 長時間開著的分頁跑的是**舊** bundle; 我們一部署, Vite 產生新 hash 的
// code-split chunk 檔、舊 chunk 檔被覆蓋掉。 舊分頁一導航到 lazy 路由 → 動態
// import 去抓那個已不存在的舊 chunk → 404 → 元件 throw → (沒有 ErrorBoundary
// 時) 整個 app unmount → 白畫面。 按重新整理會載入新的 index.html + 新 chunk → 好了。
//
// 修法: 偵測到這種 chunk 載入失敗 → 自動重新整理一次 (抓新 bundle)。 用
// sessionStorage 記時間戳, 10 秒內只重整一次 → 若真的是壞掉的 build 不會無限重整,
// 會落到 ErrorBoundary 的錯誤畫面。

export function isChunkLoadError(err: unknown): boolean {
  const e = err as { name?: string; message?: string } | null | undefined;
  if (!e) return false;
  if (e.name === 'ChunkLoadError') return true;
  const m = e.message ?? '';
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|dynamically imported module|Loading chunk [^ ]+ failed/i.test(
    m,
  );
}

/**
 * 這次 page lifetime 內是否已經觸發過重整。
 *
 * 2026-08-10 修: sessionStorage 的時間戳是**跨頁面載入**的迴圈保護 (真的壞掉的 build
 * 不會無限重整)。 但它擋不住**同一次載入內**的併發 —— `location.reload()` 是非同步的,
 * 呼叫之後頁面還會繼續跑一小段, 期間第二個 chunk 錯誤 (一頁常常有多個 lazy import)
 * 會被 10 秒節流擋掉回傳 false → ErrorBoundary 誤判成「重整救不了」→ 把錯誤畫面渲染
 * 出來。 使用者於是在重整完成前看到「頁面載入時發生問題」, 而其實重整正在路上。
 * (webfont 上線後重整要多抓 ~443KB, 這個視窗更長、更容易被看到。)
 *
 * 這個 module 層旗標補上那個縫: 已經在重整了 → 後續 chunk 錯誤一律回報「已處理」。
 * 重整完成後是全新的 module scope, 旗標自動歸零。
 */
let reloadInFlight = false;

/** 舊分頁踩到新部署 → 重新整理一次抓新 bundle。 回傳是否已由重整處理掉。 */
export function reloadOnceForStaleChunk(): boolean {
  if (reloadInFlight) return true;
  const KEY = 'ts-chunk-reload-at';
  try {
    const last = Number(sessionStorage.getItem(KEY) || '0');
    if (Date.now() - last > 10_000) {
      sessionStorage.setItem(KEY, String(Date.now()));
      reloadInFlight = true;
      window.location.reload();
      return true;
    }
  } catch {
    // sessionStorage 不可用 (隱私模式等) → 保守不重整, 交給 ErrorBoundary 顯示畫面。
  }
  return false;
}
